import { useCallback } from "react";
import useSWR from "swr";
import { Paginated, PaginationQuery } from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { NOTIFICATIONS_URL } from "./useNotifications";
import { CustomError } from "./errors";
import { ProjectSharingInvite } from "./types/projectSharingInvites";

export const useProjectSharingInvitesApi = () => {
  const api = useApi();

  const useProjectSharingInvites = useCallback(
    (projectId: string, query: PaginationQuery, signal: AbortSignal, isAdmin?: boolean) => {
      const prefix = isAdmin ? "/admin" : "";
      const url = buildUrl(`${prefix}/projects/${projectId}/sharing_invites`, query);
      return useSWR<Paginated<ProjectSharingInvite>, CustomError, string>(url, (url) =>
        api.get<Paginated<ProjectSharingInvite>>({ url, signal }),
      );
    },
    [api],
  );

  const resendProjectSharingInvite = useCallback(
    async (projectId: string, inviteId: string, isAdmin?: boolean) => {
      const prefix = isAdmin ? "/admin" : "";
      const url = `${prefix}/projects/${projectId}/sharing_invites/${inviteId}/re_send`;
      await api.post(url, {});
      mutateCache([
        `/projects`,
        `/admin/projects`,
        `/projects/${projectId}/sharing_invites`,
        `/admin/projects/${projectId}/sharing_invites`,
      ]);
    },
    [api],
  );

  const cancelProjectSharingInvite = useCallback(
    async (projectId: string, inviteId: string, isAdmin?: boolean): Promise<void> => {
      const prefix = isAdmin ? "/admin" : "";
      const url = `${prefix}/projects/${projectId}/sharing_invites/${inviteId}/cancel`;
      await api.put(url, {});
      mutateCache([
        `/projects`,
        `/admin/projects`,
        `/projects/${projectId}/sharing_invites`,
        `/admin/projects/${projectId}/sharing_invites`,
      ]);
    },
    [api],
  );

  const deleteProjectSharingInvite = useCallback(
    async (projectId: string, inviteId: string, isAdmin?: boolean): Promise<void> => {
      const prefix = isAdmin ? "/admin" : "";
      const url = `${prefix}/projects/${projectId}/sharing_invites/${inviteId}/delete`;
      await api.del(url);
      mutateCache([
        `/projects`,
        `/admin/projects`,
        `/projects/${projectId}/sharing_invites`,
        `/admin/projects/${projectId}/sharing_invites`,
      ]);
    },
    [api],
  );

  const acceptSharingInvite = useCallback(
    async (token: string): Promise<{ projectId: string }> => {
      const url = `/projects/sharing_invites/accept`;
      const result = await api.post<{ projectId: string }, { token: string }>(url, { token });
      const { projectId } = result;
      mutateCache([
        `/projects`,
        `/admin/projects`,
        `/projects/${projectId}`,
        `/admin/projects/${projectId}`,
        `/projects/${projectId}/sharings`,
        `/admin/projects/${projectId}/sharings`,
        `/projects/${projectId}/sharing_invites`,
        `/admin/projects/${projectId}/sharing_invites`,
        NOTIFICATIONS_URL,
      ]);
      return result;
    },
    [api],
  );

  return {
    useProjectSharingInvites,
    resendProjectSharingInvite,
    cancelProjectSharingInvite,
    acceptSharingInvite,
    deleteProjectSharingInvite,
  };
};
